import { ClientConfigDraft, ShowcasePalette } from "../../integrations/clientConfig";
import {
  createDarkPaletteFromAccent,
  createLightPaletteFromAccent,
} from "./themePresets";

const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const getBrightness = (r: number, g: number, b: number) =>
  (r * 299 + g * 587 + b * 114) / 1000;

const getSaturation = (r: number, g: number, b: number) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (max === 0) {
    return 0;
  }

  return (max - min) / max;
};

const toHex = (value: number) => clamp(value).toString(16).padStart(2, "0");
const rgbToHex = (r: number, g: number, b: number) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(
        new Error(
          "Не удалось прочитать логотип. Для внешнего URL проверь CORS или загрузи файл напрямую.",
        ),
      );
    image.src = src;
  });

const getImageData = async (src: string) => {
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  if (!ctx) {
    throw new Error("Canvas недоступен для анализа логотипа.");
  }

  const maxWidth = 160;
  const scale = Math.min(1, maxWidth / Math.max(image.naturalWidth, 1));
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
};

export const detectPaletteFromLogo = async (
  src: string,
  tone: "dark" | "light" = "dark",
): Promise<{
  palette: ShowcasePalette;
  logoTextColor: string;
}> => {
  const data = await getImageData(src);

  let bestScore = -1;
  let bestColor: [number, number, number] = [155, 173, 222];

  for (let index = 0; index < data.length; index += 4 * 4) {
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const alpha = data[index + 3];

    if (alpha < 140) {
      continue;
    }

    const saturation = getSaturation(r, g, b);
    const brightness = getBrightness(r, g, b);
    const vividnessPenalty = Math.abs(brightness - 150) / 255;
    const score = saturation * 0.75 + (1 - vividnessPenalty) * 0.25;

    if (score > bestScore) {
      bestScore = score;
      bestColor = [clamp(r), clamp(g), clamp(b)];
    }
  }

  const palette =
    tone === "light"
      ? createLightPaletteFromAccent(bestColor)
      : createDarkPaletteFromAccent(bestColor);

  const logoTextColor =
    tone === "light"
      ? rgbToHex(
          clamp(bestColor[0] * 0.72),
          clamp(bestColor[1] * 0.72),
          clamp(bestColor[2] * 0.72),
        )
      : rgbToHex(
          clamp(bestColor[0] + (255 - bestColor[0]) * 0.65),
          clamp(bestColor[1] + (255 - bestColor[1]) * 0.65),
          clamp(bestColor[2] + (255 - bestColor[2]) * 0.65),
        );

  return {
    palette,
    logoTextColor,
  };
};
